<template>
    <DForm @submit.prevent="submitForm">
        <DFormRow
            :label="__('settings.antagonist_settings.form.url')"
            :is-required="true"
            :error="errorsData.url"
            :hint="__('settings.antagonist_settings.form.url_hint')"
        >
            <DInput
                id="url"
                v-model="form.url"
                :placeholder="__('settings.antagonist_settings.form.url')"
                :is-valid="isFormFieldValid('url')"
                maxlength="128"
            />
        </DFormRow>
        <DFormRow
            :label="__('settings.antagonist_settings.form.username')"
            :is-required="true"
            :error="errorsData.username"
            :hint="__('settings.antagonist_settings.form.username_hint')"
        >
            <DInput
                id="username"
                v-model="form.username"
                :placeholder="__('settings.antagonist_settings.form.username')"
                :is-valid="isFormFieldValid('username')"
            />
        </DFormRow>
        <DFormRow
            :label="__('settings.antagonist_settings.form.password')"
            :is-required="antagonistSettings === null"
            :error="errorsData.password"
            :hint="__('settings.antagonist_settings.form.password_hint')"
        >
            <DInput
                id="password"
                v-model="form.password"
                :placeholder="__('settings.antagonist_settings.form.password')"
                type="password"
                :is-valid="isFormFieldValid('password')"
                maxlength="255"
            />
        </DFormRow>
        <DFormRow
            :label="__('settings.antagonist_settings.form.tenant_id')"
            :is-required="true"
            :error="errorsData.tenant_id"
            :hint="__('settings.antagonist_settings.form.tenant_id_hint')"
        >
            <DInput
                id="tenant_id"
                v-model="form.tenant_id"
                :placeholder="__('settings.antagonist_settings.form.tenant_id')"
                :is-valid="isFormFieldValid('tenant_id')"
                type="string"
            />
        </DFormRow>
        <DRow>
            <DCol sm="4" />
            <DCol sm="4">
                <div
                    v-if="connectionStatus === 'successful'"
                    class="connection-successful"
                >
                    {{ __('settings.antagonist_settings.connection_successful') }}
                </div>
                <div
                    v-if="connectionStatus === 'unsuccessful'"
                    class="connection-failed"
                >
                    {{ __('settings.antagonist_settings.connection_unsuccessful') }}
                </div>
            </DCol>
            <DCol
                sm="4"
                class="check-database-connection-btn"
            >
                <DButton
                    icon="lan-connect"
                    :label="__('settings.antagonist_settings.check_connection')"
                    :disabled="isInLoadingState"
                    @click="checkConnection"
                />
            </DCol>
        </DRow>
        <DFormControls
            :dirty="form.isDirty"
            @submit="submitForm"
        />
        <DConnectionErrorModal
            :show-modal="connectionException !== null"
            :exception="connectionException"
            @close-modal="closeErrorCheckModal"
        />
    </DForm>
</template>

<script setup>
import DConnectionErrorModal from '~@/Components/Common/DConnectionErrorModal'
import { useForm } from '@inertiajs/vue3'

import { route } from '~@/Composables/route'
import { ref, watch } from 'vue'

const props = defineProps({
    antagonistSettings: {
        type: Object,
        default: () => { return {} }
    },
    errors: {
        type: Object,
        default: () => { return {} }
    }
})

const emit = defineEmits(['form-submitted'])

const form = useForm({
    url: props.antagonistSettings?.url ?? null,
    username: props.antagonistSettings?.username ?? null,
    password: props.antagonistSettings?.password ?? null,
    tenant_id: props.antagonistSettings?.tenant_id ?? null,
    finance_system: 'antagonist_finance'
})

const connectionStatus = ref('')
const apiError = ref(null)
const connectionException = ref(null)
const credentialsAreValid = ref(false)
const errorsData = ref(props.errors)

watch(() => props.errors, (newErrors, oldErrors) => {
    errorsData.value = newErrors
})

function checkConnection () {
    errorsData.value = {}
    connectionException.value = null
    window.axios.post(route('settings.finance-system-settings.check-antagonist', form))
        .then(function () {
            connectionStatus.value = 'successful'
            credentialsAreValid.value = true
        })
        .catch(function (error) {
            connectionStatus.value = 'unsuccessful'
            credentialsAreValid.value = false
            if (error.response.data.api_error !== undefined) {
                apiError.value = error.response.data.api_error
            }

            if (error.response.data.errors !== undefined) {
                errorsData.value = error.response.data.errors
                for (const key in errorsData.value) {
                    if (typeof errorsData.value[key] === 'object') {
                        errorsData.value[key] = errorsData.value[key][0]
                    }
                }
            }

            if (error?.response.data.exception !== undefined) {
                connectionException.value = error.response.data.exception
            }
        })
}

function closeErrorCheckModal () {
    connectionException.value = null
}

function submitForm () {
    emit('form-submitted', form)
}
</script>

<style lang="scss" scoped>
.check-database-connection-btn {
    text-align: right;
    padding-bottom: 25px;
}
.connection-successful {
    color: green;
}
.connection-failed {
    color: red;
}
</style>
